import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { SxProps } from '@mui/system/styleFunctionSx';

import copyToClipboard from 'copy-to-clipboard';

import CheckmarkIcon from '@watershed/icons/components/Checkmark';
import CopyIcon from '@watershed/icons/components/Copy';
import mixinSx from '@watershed/style/mixinSx';
import { tooltipDarkStyleProps } from '@watershed/style/tooltips';

import type { MuiTooltipProps } from '../utils/MaybeTooltip';
import Button, { ButtonProps } from './Button';
import IconButton from './IconButton';

export default function CopyButton({
  color,
  size,
  icon,
  iconOnly = true,
  label = 'Copy',
  tooltipTextOnCopy = 'Copied to clipboard',
  showTooltip = true,
  tooltipTimeout = 1500,
  tooltipPlacement = 'bottom',
  onCopy,
  sx,
  text,
  textFormat,
  variant,
  disabled,
}: {
  icon?: React.ReactNode;
  iconOnly?: boolean;
  label?: LocalizedString;
  showTooltip?: boolean;
  onCopy?: () => void;
  tooltipTextOnCopy?: LocalizedString;
  tooltipPlacement?: MuiTooltipProps['placement'];
  tooltipTimeout?: number;
  sx?: SxProps<Theme>;
  text: LocalizedString;
  textFormat?: 'text/html' | 'text/plain';
} & Pick<ButtonProps, 'color' | 'size' | 'variant' | 'disabled'>) {
  const { t } = useLingui();

  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>(label);
  const minSize = size === 'large' ? 32 : 24;
  const iconSize = size === 'large' ? 16 : 12;
  const defaultIcon = copySuccess ? (
    // in the case of iconOnly, we don't just want the button to disappear
    // but if there is text (i.e. not iconOnly) then we are okay with the icon disappearing
    iconOnly ? (
      <CheckmarkIcon size={iconSize} />
    ) : null
  ) : (
    <CopyIcon size={iconSize} />
  );

  useEffect(() => {
    if (copySuccess) {
      setTooltipText(tooltipTextOnCopy);
      const timer = setTimeout(() => {
        setCopySuccess(false);
        setTooltipText(label);
      }, tooltipTimeout);

      return () => clearTimeout(timer);
    }
  }, [copySuccess, tooltipTextOnCopy, label, tooltipTimeout]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    copyToClipboard(text, { format: textFormat });
    setCopySuccess(true);
    if (onCopy) onCopy();
  };

  return (
    <Tooltip
      title={showTooltip ? tooltipText : ''}
      placement={tooltipPlacement}
      slotProps={tooltipDarkStyleProps()}
    >
      {iconOnly ? (
        <IconButton
          aria-label={`copy ${text} to clipboard`}
          color={color}
          onClick={handleClick}
          size={size}
          sx={mixinSx(
            { minWidth: minSize, minHeight: minSize },
            color === 'secondary' && {
              '& svg': {
                color: (theme) => theme.palette.grey50,
                transition: (theme) => theme.transitions.create('color'),
              },
              '&:hover svg': {
                color: (theme) => theme.palette.grey100,
              },
            },
            sx
          )}
          disabled={disabled}
        >
          {icon ?? defaultIcon}
        </IconButton>
      ) : (
        <Button
          color={color}
          endIcon={icon ?? defaultIcon}
          aria-label={`copy ${text} to clipboard`}
          onClick={handleClick}
          sx={sx}
          size={size}
          variant={variant}
          disabled={disabled}
        >
          {copySuccess
            ? t({
                message: 'Copied!',
                context:
                  'Shown after clicking a button to copy text to clipboard',
              })
            : label}
        </Button>
      )}
    </Tooltip>
  );
}
